import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Select, DatePicker } from "antd";
import { ArrowLeftRight } from "lucide-react";
import { DialogModal, ConfirmPopupModal } from "../../../components/ui/dialogModal";
import { Textarea } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import useApiMethods from "../../../lib/useApiMethods";
import ToastMsg from "../../../components/common/ToastMsg";
import { SHIFT_CHANGE_REQUEST } from "../../../constants/api";
import {
    MAX_BACKDATE_DAYS,
    apiErrorMessage,
    buildShiftOptionLabel,
    disableShiftChangeDate,
} from "./shiftChangeUtils";

const emptyForm = (date) => ({
    date: date || dayjs().format("YYYY-MM-DD"),
    requested_shift_id: "",
    reason: "",
});

const ShiftChangeRequestDialog = ({
    open,
    onClose,
    onSuccess,
    initialDate = null,
    lockDate = false,
    currentShiftLabel = null,
    hasAttendance = true,
}) => {
    const { fetch, create, update } = useApiMethods();
    const [loading, setLoading] = useState(false);
    const [shifts, setShifts] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [form, setForm] = useState(emptyForm(initialDate));

    const shiftOptions = useMemo(
        () => shifts.map((s) => ({ value: s.id, label: buildShiftOptionLabel(s) })),
        [shifts],
    );

    const resetState = useCallback((date) => {
        setEditingId(null);
        setForm(emptyForm(date || initialDate || dayjs().format("YYYY-MM-DD")));
    }, [initialDate]);

    const loadShifts = useCallback(async () => {
        const res = await fetch(SHIFT_CHANGE_REQUEST, { shifts_only: true });
        if (res?.status !== "success") {
            setShifts([]);
            return;
        }
        const list = Array.isArray(res.data) ? res.data : (res?.data?.result || []);
        setShifts(Array.isArray(list) ? list : []);
    }, [fetch]);

    const loadPendingForDate = useCallback(async (dateStr) => {
        if (!dateStr) return null;
        const res = await fetch(SHIFT_CHANGE_REQUEST, {
            date: dateStr,
            status: "pending",
            mine_only: true,
        });
        if (res?.status !== "success") return null;
        const rows = Array.isArray(res.data) ? res.data : (res?.data?.result || []);
        return rows.find((row) => row.status === "pending") || rows[0] || null;
    }, [fetch]);

    useEffect(() => {
        if (!open) return;

        const dateStr = initialDate || dayjs().format("YYYY-MM-DD");
        resetState(dateStr);

        (async () => {
            setLoading(true);
            try {
                await loadShifts();
                const pending = await loadPendingForDate(dateStr);
                if (pending) {
                    setEditingId(pending.id);
                    setForm({
                        date: pending.date || dateStr,
                        requested_shift_id: pending.requested_shift_id || "",
                        reason: pending.reason || "",
                    });
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [open, initialDate, loadShifts, loadPendingForDate, resetState]);

    const handleClose = () => {
        setCancelOpen(false);
        onClose?.();
    };

    const validateForm = () => {
        if (!form.date || !form.requested_shift_id || !form.reason.trim()) {
            ToastMsg("error", "Date, shift, and reason are required");
            return false;
        }
        if (form.reason.trim().length < 10) {
            ToastMsg("error", "Reason must be at least 10 characters");
            return false;
        }
        if (!hasAttendance) {
            ToastMsg("error", "Attendance must exist for this date before requesting a shift change");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);
            const payload = editingId
                ? {
                    id: editingId,
                    requested_shift_id: form.requested_shift_id,
                    reason: form.reason.trim(),
                }
                : {
                    date: form.date,
                    requested_shift_id: form.requested_shift_id,
                    reason: form.reason.trim(),
                };

            const res = editingId
                ? await update(SHIFT_CHANGE_REQUEST, payload)
                : await create(SHIFT_CHANGE_REQUEST, payload);

            if (res?.status === "success") {
                ToastMsg("success", res.message || (editingId ? "Request updated" : "Request submitted"));
                handleClose();
                onSuccess?.();
            } else {
                ToastMsg("error", apiErrorMessage(res, "Failed to save request"));
            }
        } catch (err) {
            ToastMsg("error", apiErrorMessage(err, "Failed to save request"));
        } finally {
            setLoading(false);
        }
    };

    const handleCancelRequest = async () => {
        if (!editingId) return;
        try {
            setLoading(true);
            const res = await update(SHIFT_CHANGE_REQUEST, { id: editingId, status: "cancelled" });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Request cancelled");
                setCancelOpen(false);
                handleClose();
                onSuccess?.();
            } else {
                ToastMsg("error", apiErrorMessage(res, "Failed to cancel request"));
            }
        } catch (err) {
            ToastMsg("error", apiErrorMessage(err, "Failed to cancel request"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <DialogModal
                open={open}
                onClose={handleClose}
                onSave={handleSubmit}
                saveLabel={editingId ? "Update" : "Submit"}
                loading={loading}
                title={editingId ? "Edit Shift Change Request" : "Request Shift Change"}
                width="520px"
            >
                <div className="space-y-3">
                    {currentShiftLabel && (
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 px-3 py-2.5">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Current shift</p>
                            <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                <ArrowLeftRight className="h-3.5 w-3.5 text-sidebar-primary shrink-0" />
                                {currentShiftLabel}
                            </p>
                        </div>
                    )}

                    {!hasAttendance && (
                        <p className="text-xs text-amber-700 dark:text-amber-300 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2">
                            No attendance record exists for this date. Shift change can only be requested when attendance is present.
                        </p>
                    )}

                    <div>
                        <label className="text-xs font-medium">Date</label>
                        <DatePicker
                            className="w-full"
                            size="large"
                            format="DD-MM-YYYY"
                            value={form.date ? dayjs(form.date) : null}
                            onChange={(value) => setForm((f) => ({
                                ...f,
                                date: value ? value.format("YYYY-MM-DD") : "",
                            }))}
                            disabledDate={disableShiftChangeDate}
                            getPopupContainer={() => document.body}
                            allowClear={false}
                            disabled={lockDate || Boolean(editingId) || loading}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            {lockDate || editingId
                                ? "Date is fixed for this request."
                                : `Today or up to ${MAX_BACKDATE_DAYS} days back.`}
                        </p>
                    </div>

                    <div>
                        <label className="text-xs font-medium">Requested shift</label>
                        <Select
                            size="large"
                            className="w-full"
                            placeholder="Select shift"
                            options={shiftOptions}
                            value={form.requested_shift_id || undefined}
                            onChange={(v) => setForm((f) => ({ ...f, requested_shift_id: v }))}
                            disabled={loading || !hasAttendance}
                        />
                    </div>

                    <div>
                        <label className="text-xs font-medium">Reason</label>
                        <Textarea
                            value={form.reason}
                            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                            rows={4}
                            maxLength={500}
                            showCharCount
                            disabled={loading || !hasAttendance}
                            placeholder="Explain why the assigned shift was incorrect (min 10 characters)"
                        />
                    </div>

                    {editingId && (
                        <div className="pt-1 border-t border-gray-200 dark:border-gray-700">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={loading}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => setCancelOpen(true)}
                            >
                                Cancel this request
                            </Button>
                        </div>
                    )}
                </div>
            </DialogModal>

            <ConfirmPopupModal
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                onConfirm={handleCancelRequest}
                title="Cancel shift change request?"
                description={`This will cancel your pending request for ${form.date ? dayjs(form.date).format("DD MMM YYYY") : "this date"}.`}
                type="danger"
                confirmLabel="Cancel request"
                cancelLabel="Keep"
                loading={loading}
                destructive
            />
        </>
    );
};

export default ShiftChangeRequestDialog;
